import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";
import { colors, glyphs, frames } from "../../../Styles";
import { PageHeader, SystemControls, CardNumberDisplay } from "../../../Components";
import { Graph_QL, useSystem, useSection } from "../../../Services";
import { useAppSelector } from "../../../Store";

export default function FW_FreedaLF() {
  const { t } = useTranslation();
  const focus_view = true;

  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const location = useLocation();
  const systemId = useMemo(
    () => (urlSystemId ? +urlSystemId : location?.state?.selected),
    [urlSystemId, location]
  );

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const { call: getSystemCall, data: systemsState } = useSystem(connection);
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);

  useEffect(() => {
    getSystemCall({ ids: [systemId] })
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line
  }, []);

  // Get System Name for Header
  const systemName = useMemo(() => systemsState[domain]?.data.find((system) => system.system_id === systemId)?.name, [domain, systemId, systemsState]);

  // Get System Data
  const systemSectionsData = useMemo(() => {
    if (lfSectionsState[domain]) {
      return lfSectionsState[domain].data.filter(s => s.system_id === systemId);
    }
    return [];
  }, [domain, lfSectionsState, systemId]);
  
  const { numberOfSections, totalNumberOfPens, totalNumberOfAnimals } = useMemo(() => {
    const _numberOfSections = systemSectionsData.length;
    const [_totalNumberOfPens, _totalNumberOfAnimals] = systemSectionsData.reduce(
      (accumulator, currentSection) => {
        accumulator[0] += currentSection.section_locations.length;
        accumulator[1] += currentSection.section_locations.reduce(
          (innerAccumulator, currentLocation) => {
            innerAccumulator += currentLocation.animals.length;
            return innerAccumulator;
          },
          0
        );
        return accumulator;
      },
      [0, 0]
    );
    return {
      numberOfSections: _numberOfSections,
      totalNumberOfPens: _totalNumberOfPens,
      totalNumberOfAnimals: _totalNumberOfAnimals
    };
  }, [systemSectionsData]);

  // Translations
  const translations = useMemo(() => ({
    sections: t("locationFeederPages.locationFeeder.sections", { defaultValue: "Sections" }),
    pens: t("locationFeederPages.locationFeeder.pens", { defaultValue: "Pens" }),
    animals: t("locationFeederPages.locationFeeder.animals", { defaultValue: "Animals" })
  }), [t])

  // Card Displays
  const memoizedCardDisplay = useMemo(() => (
    <div
      style={{ display: "flex", flexWrap: "wrap", flexDirection: "row", gap: "10px", boxSizing: "border-box", alignItems: "center", width: "100%" }}>
      <CardNumberDisplay
        focus_view={focus_view}
        value={numberOfSections}
        unit={translations.sections}
        glyph={ <glyphs.AnimalGroup fillColor={colors.greySecondary} size="150px" /> }
      />
      <CardNumberDisplay
        focus_view={focus_view}
        value={totalNumberOfPens}
        unit={translations.pens}
        glyph={ <glyphs.Condition fillColor={colors.greySecondary} size="150px" /> }
      />
      <CardNumberDisplay
        focus_view={focus_view}
        value={totalNumberOfAnimals}
        unit={translations.animals}
        glyph={ <glyphs.Animal fillColor={colors.greySecondary} size="150px" /> }
      />
    </div>
  ), [focus_view, numberOfSections, translations.sections, translations.pens, translations.animals, totalNumberOfPens, totalNumberOfAnimals]);

  return(
    <frames.Page_inner_focus_view>
        <PageHeader topHeader={systemName} />
      
      <frames._page_inner_content_scroll>
        <div style={{display: "flex", flexDirection: "column", gap: "20px", margin: "0 auto", width: "100%", alignItems: "center", justifyContent: "center"}}>
          {memoizedCardDisplay}
          <SystemControls systemId={systemId} focus_view/>
        </div>
      </frames._page_inner_content_scroll>
    </frames.Page_inner_focus_view>
  )
}